import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3 } from 'lucide-react';
import { CouponBrowser } from '../CouponBrowser';

function StatCard({ icon: Icon, value, label, accent, testId }) {
  return (
    <Card className="hero-card" data-testid={testId}>
      <CardContent className="pt-4 pb-4 text-center">
        <Icon className={`h-5 w-5 mx-auto mb-1.5 ${accent || ''}`} />
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs text-slate-300">{label}</p>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ value, max, color = 'bg-emerald-500' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="w-full bg-slate-200 rounded-full h-2">
      <div className={`${color} h-2 rounded-full progress-bar-fill`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function AdminOverviewTab({ stats, categoryAnalytics, influencerAnalytics, Icons }) {
  const { Users, UserCog, TrendingUp, Tag, ShoppingBag, CheckCircle } = Icons;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 sm:gap-4">
        <StatCard icon={Users} value={stats.total_users} label="Usuarios" testId="stat-users" />
        <StatCard icon={UserCog} value={stats.total_merchants} label="Lojistas" testId="stat-merchants" />
        <StatCard icon={TrendingUp} value={stats.total_influencers || 0} label="Influenciadores" testId="stat-influencers" />
        <StatCard icon={Tag} value={stats.total_coupons} label="Cupons Total" testId="stat-coupons" />
        <StatCard icon={Tag} value={stats.active_coupons} label="Cupons Ativos" testId="stat-active" />
        <StatCard icon={ShoppingBag} value={stats.total_claims} label="Resgates" testId="stat-claims" />
        <StatCard icon={CheckCircle} value={stats.total_validations} label="Validacoes" testId="stat-validations" />
      </div>

      <Card className="bg-white border border-slate-200">
        <CardHeader><CardTitle className="text-lg text-slate-900">Distribuicao de Planos</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(stats.plans_count || {}).map(([plan, count]) => (
              <div key={plan} className="text-center p-4 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-2xl font-bold text-slate-900">{count}</p>
                <p className="text-sm text-slate-600 capitalize">{plan}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200">
        <CardHeader><CardTitle className="text-lg text-slate-900">Funil de Conversao</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { label: 'Cupons Criados', value: stats.total_coupons, color: 'bg-blue-500' },
              { label: 'Resgates (Enviados)', value: stats.total_claims, color: 'bg-amber-500' },
              { label: 'Validacoes (Confirmados)', value: stats.total_validations, color: 'bg-emerald-500' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-4">
                <span className="text-sm text-slate-600 w-48 shrink-0">{item.label}</span>
                <ProgressBar value={item.value} max={stats.total_coupons} color={item.color} />
                <span className="text-sm font-bold text-slate-900 w-12 text-right">{item.value}</span>
              </div>
            ))}
            <div className="pt-2 border-t border-slate-100 flex justify-between text-sm">
              <span className="text-slate-500">Taxa de conversao (resgates &rarr; validacoes)</span>
              <span className="font-bold text-emerald-600">
                {stats.total_claims > 0 ? Math.round(stats.total_validations / stats.total_claims * 100) : 0}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {categoryAnalytics.length > 0 && (
        <Card className="bg-white border border-slate-200">
          <CardHeader><CardTitle className="text-lg text-slate-900">Categorias Mais Populares</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {categoryAnalytics.slice(0, 5).map(cat => (
                <div key={cat.category} className="flex items-center gap-4">
                  <span className="text-sm text-slate-700 w-40 shrink-0 truncate">{cat.category}</span>
                  <ProgressBar value={cat.total_claims} max={categoryAnalytics[0]?.total_claims || 1} color="bg-blue-500" />
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-slate-500">{cat.total_claims} resgates</span>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">{cat.conversion_rate}%</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {influencerAnalytics.length > 0 && (
        <Card className="bg-white border border-slate-200">
          <CardHeader><CardTitle className="text-lg text-slate-900">Resumo de Influenciadores</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                <p className="text-2xl font-bold text-indigo-700">{influencerAnalytics.length}</p>
                <p className="text-xs text-indigo-500">Bolhas</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
                <p className="text-2xl font-bold text-green-700">{influencerAnalytics.reduce((s,i) => s + i.active_merchants, 0)}</p>
                <p className="text-xs text-green-500">Lojistas Ativos</p>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg border border-amber-100">
                <p className="text-2xl font-bold text-amber-700">R$ {influencerAnalytics.reduce((s,i) => s + i.total_commission, 0).toFixed(2)}</p>
                <p className="text-xs text-amber-500">Comissao Total</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg border border-slate-100">
                <p className="text-2xl font-bold text-slate-700">{influencerAnalytics.reduce((s,i) => s + i.total_coupons, 0)}</p>
                <p className="text-xs text-slate-500">Cupons Gerados</p>
              </div>
            </div>
            <div className="space-y-2">
              {influencerAnalytics.slice(0, 5).map(inf => (
                <div key={inf.user_id} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-50">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-900">{inf.name}</span>
                    {inf.is_virtual && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Virtual</Badge>}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-500">{inf.active_merchants} ativos</span>
                    <span className="text-xs font-bold text-amber-700">R$ {inf.total_commission.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border border-slate-200" data-testid="admin-coupon-browser-section">
        <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-blue-500" /> Buscar Cupons</CardTitle></CardHeader>
        <CardContent>
          <CouponBrowser />
        </CardContent>
      </Card>
    </div>
  );
}
